import React, { useMemo } from "react";

import { ProgressBar, Spinner, Text } from "@performyard/py-components";

const AverageIncreaseWidget = ({
	title,
	counter,
	description,
	displayMoney = false,
	displayPercent = false,
	isLoading = false,
	testId = "widget",
}: {
	title: string;
	counter: number;
	description: string[];
	displayMoney?: boolean;
	displayPercent?: boolean;
	isLoading?: boolean;
	testId?: string;
}) => {
	const counterDisplay = useMemo(() => {
		const sign = counter < 0 ? "-" : "";
		const suffixes = ["", "K", "M", "B"];
		let valueCounter = Math.abs(counter);
		let suffixIndex = 0;
		while (valueCounter >= 1000) {
			valueCounter /= 1000;
			suffixIndex++;
		}
		let counterValue = valueCounter.toFixed(2);
		let counterAbbreviation = suffixes[suffixIndex];

		if (counterAbbreviation === undefined) {
			counterValue = Math.abs(counter).toExponential(0);
			counterAbbreviation = "";
		}

		return `${sign}${counterValue}${counterAbbreviation}`;
	}, [counter]);

	return (
		<div
			data-testid={testId}
			className="box-border flex h-[64px] max-h-[112px] w-full min-w-0 flex-col justify-center gap-0 rounded-md border border-solid border-neutral-2 bg-neutral-1 p-4"
		>
			<Text size="caption" weight="regular" color="neutral-5" dangerouslySetClassName="leading-tight">
				{title}
			</Text>
			{isLoading ? (
				<div className="flex w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			) : (
				<div className="flex flex-col gap-0 leading-tight">
					<Text
						size="paragraph-1"
						weight="semi-bold"
						color="neutral-9"
						dangerouslySetClassName="leading-tight"
					>
						{displayMoney ? "$" : null}
						{counterDisplay}
						{displayPercent ? "%" : null}
					</Text>
					<Text size="caption" weight="regular" color="neutral-5" dangerouslySetClassName="leading-tight">
						{description.join(" ")}
					</Text>
				</div>
			)}
		</div>
	);
};

type Buckets = {
	[key: string]: {
		value: number | string;
		color: string;
		unit: string;
		valueCount: number;
	};
};

const ProgressBarBuckets = ({ buckets }: { buckets: Buckets }) => {
	const bucketArray = Object.entries(buckets);
	return (
		<div className="w-full">
			{bucketArray.map(([label, data]) => {
				const barValue = typeof data.value === "string" ? Number.parseFloat(data.value) : data.value;
				const colorOverride = data.color.startsWith("#") ? data.color : `#${data.color}`;
				return (
					<div key={label} className="w-full">
						<div className="mt-1 mb-1 flex w-full flex-row justify-between gap-2">
							<Text size="caption" weight="regular" color="neutral-5">
								{label}%
							</Text>
							<Text size="caption" weight="regular" color="neutral-5">
								{data.valueCount} {data.valueCount === 1 ? data.unit : `${data.unit}s`}
							</Text>
						</div>
						<ProgressBar colorOverride={colorOverride} value={Number.isFinite(barValue) ? barValue : 0} />
					</div>
				);
			})}
		</div>
	);
};

export const AverageCompensationIncreaseWidget = ({
	avgApprovedPerEmployee,
	averageApprovedPercentIncrease,
	medianApprovedIncreasePerEmployee,
	buckets,
}: {
	avgApprovedPerEmployee: number;
	averageApprovedPercentIncrease: number;
	medianApprovedIncreasePerEmployee: number;
	buckets: Buckets;
}) => {
	return (
		<div className="flex w-full flex-col gap-2">
			<div className="grid w-full grid-cols-3 gap-2">
				<AverageIncreaseWidget
					title="Avg. Increase"
					counter={avgApprovedPerEmployee}
					description={["per employee"]}
					displayMoney={true}
				/>
				<AverageIncreaseWidget
					title="Avg. % Raise"
					counter={averageApprovedPercentIncrease}
					description={["of base salary"]}
					displayPercent={true}
				/>
				<AverageIncreaseWidget
					title="Median Increase"
					counter={medianApprovedIncreasePerEmployee}
					description={["per employee"]}
					displayMoney={true}
				/>
			</div>
			<ProgressBarBuckets buckets={buckets}></ProgressBarBuckets>
		</div>
	);
};
